import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { Button } from './Button';
import { CHECKOUT_URL, HERO_IMAGE, HERO_IMAGE_ALT } from '../data/offer';
import { trackCheckout } from '../utils/tracking';
const negatives = ['Not a random collection of sewing videos.', 'Not a pile of old class handouts.', 'Not a text-heavy book you have to turn into lessons.', 'Not a pile of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE SYSTEM.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'TEACH THE CLASS.', 'RUN THE PRACTICE.', 'CHECK THE SKILL.'];
const teachingAreas = [{
  number: '01',
  step: 'SEWING FUNDAMENTALS, TOOLS, FABRICS & MACHINE TECHNIQUES',
  detail: 'Studio setup, tools, machine parts, threading, needles, tension, fabrics, hand stitches and seams.'
}, {
  number: '02',
  step: 'PATTERN MAKING, BODY MEASUREMENTS & GARMENT DRAFTING',
  detail: 'Measuring, ease, size charts, pattern symbols, seam allowances and skirt, bodice, sleeve and trouser blocks.'
}, {
  number: '03',
  step: 'ADVANCED PATTERN MAKING, DRAPING & GARMENT FITTING',
  detail: 'Dart manipulation, collars, sleeves, draping on the dress form, fitting adjustments and grading.'
}, {
  number: '04',
  step: 'GARMENT CUTTING, ASSEMBLY & CONSTRUCTION TECHNIQUES',
  detail: 'Layouts, cutting, interfacing, collars, sleeves, waistbands, pockets, zippers, closures and hems.'
}, {
  number: '05',
  step: 'PROFESSIONAL TAILORING, FINISHING TECHNIQUES & GARMENT ALTERATIONS',
  detail: 'Linings, canvas, pad stitching, tailored collars, pressing, alterations, repairs and quality control.'
}, {
  number: '06',
  step: 'SEWING INSTRUCTION, TEACHING PROGRAMS & PRACTICAL ASSESSMENTS',
  detail: 'Lesson design, demonstrations, project banks, course programs, checklists, rubrics and certificates.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-[#F8F3EC] relative overflow-hidden" data-id="element-282" data-mp="2hq78c4cdt9oi">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-[#C9A24A]" data-id="element-283" data-mp="2csnuv6nid0xm" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-284" data-mp="2qk2bx7xuus21">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-285" data-mp="2qln6uyo30d0f">
          <p className="text-sm md:text-base font-semibold text-[#8A6A2B] mb-4" data-id="element-286" data-mp="1yuh4ebkw237z">
            The Complete Visual Sewing Training System for Sewing Instructors, Pattern-Making Teachers &amp; Sewing Academies
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-[#520128]" data-id="element-287" data-mp="22tya964892do">
            SEWING &amp; PATTERN MAKING INSTRUCTOR <span className="text-[#8E2C5C]" data-id="element-288" data-mp="3j76njvrge4ms">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-[#520128] font-semibold mb-6" data-id="element-289" data-mp="3e9df0w2ogyjc">
            A Complete 6-IN-1 Visual Sewing, Pattern Making &amp; Garment Construction System
          </p>
          <img src={HERO_IMAGE} alt={HERO_IMAGE_ALT} className="w-full max-w-sm h-auto mx-auto mb-6 drop-shadow-xl" loading="lazy" data-id="element-290" data-mp="3qrqzxmlkz71w" />
          <p className="text-base md:text-lg text-[#8A6A2B] font-semibold mb-10" data-id="element-291" data-mp="3cuaofwofgn3a">
            6 PROFESSIONAL VOLUMES · 300 VISUAL TRAINING PAGES · 5 EXCLUSIVE BONUSES
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-292" data-mp="eac9nva0hxev">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-[#E6D5BF]" data-id="element-293" data-mp="e9stok3x9j3h">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-294" data-mp="x0jxl6554hah">
                <X className="text-[#520128]/50 shrink-0 mt-1" size={18} data-id="element-295" data-mp="x2wow9wsg407" />
                <p className="text-slate-700/80 text-sm" data-id="element-296" data-mp="34lya493al08t">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-[#520128]" data-id="element-297" data-mp="30l5yxaav0l9w">
            <div className="flex items-start gap-3" data-id="element-298" data-mp="3oagrw5lyxbdh">
              <Check className="text-[#8E2C5C] shrink-0 mt-1" size={20} data-id="element-299" data-mp="3j9178enpd6vm" />
              <div className="space-y-3" data-id="element-300" data-mp="3eib1h0bd720e">
                <p className="text-[#520128] font-medium text-base leading-relaxed" data-id="element-301" data-mp="3eh9o2p6vm3fi">
                  SEWING &amp; PATTERN MAKING INSTRUCTOR ATLAS is a complete visual training system for sewing
                  instructors, pattern-making teachers, sewing academies and studio workshops — from beginner to
                  advanced.
                </p>
                <p className="text-[#8A6A2B] font-semibold text-sm leading-relaxed" data-id="element-302" data-mp="de5obcs0v9ir">
                  Every page gives you an illustration, labeled diagram or construction sequence, a step-by-step
                  breakdown, a ❌ common mistake vs. ✅ correct technique comparison, a teaching cue, a drill,
                  a quick check and an Easy / Standard / Advanced variant — all in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-303" data-mp="11buwsiqzrixt">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-304" data-mp="11at99essh3z7">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-[#520128]" data-id="element-305" data-mp="163cvt19gf9g5">
                {line}
              </li>)}
          </ul>
          <p className="mt-8 text-lg md:text-xl font-bold text-[#520128]" data-id="element-306" data-mp="1g224q4jdhjk9">
            Make Your Next Sewing Class Easier to Prepare and More Visual to Teach.
          </p>
          <div className="flex justify-center mt-5" data-id="element-307" data-mp="3h2grfggjz5q1">
            <Button href={CHECKOUT_URL} onLinkClick={trackCheckout} className="text-sm sm:text-lg py-3 sm:py-4 px-5 sm:px-8" data-id="element-308" data-mp="3h0vzzzpjc2we">
              GET MY COMPLETE 6-IN-1 SEWING TRAINING SYSTEM NOW!
            </Button>
          </div>
        </div>

        <div className="mt-12" data-id="element-309" data-mp="3l2h7t6n7pqzq">
          <p className="text-[#8A6A2B] text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-310" data-mp="3nu05gybkhci">
            The System Covers Six Essential Sewing Teaching Areas
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-311" data-mp="3uwho84ny2inr">
            {teachingAreas.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-[#E6D5BF]" data-id="element-312" data-mp="8nge5slz21md">
                <span className="text-sm font-bold text-[#8A6A2B] mb-1" data-id="element-313" data-mp="2ehygi5t5xl07">{item.number}</span>
                <span className="text-lg md:text-xl font-extrabold tracking-tight leading-tight text-[#520128]" data-id="element-314" data-mp="2agmq3y2gsvqy">
                  {item.step}
                </span>
                <span className="text-sm text-slate-700/80 mt-1" data-id="element-315" data-mp="24awy0l7k9jxl">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-[#520128]" data-id="element-316" data-mp="1qipe3u1dlngb">
            SIX AREAS. SIX COMPLETE VOLUMES. ONE PROFESSIONAL TRAINING SYSTEM.
          </p>
        </div>
      </div>
    </section>;
}